import apiClient from '@/lib/api-client';
import { createDateFromUTCString } from '@/lib/date-util';
import { ApiError } from '@/model/ApiError';
import {
    createEventRequestFromFormData,
    DuplicateEventRequest,
    DuplicateEventSource,
    EventFormData,
    EventFlowSettingsUpdate,
    EventRequest,
    EventResponse,
    EventUpdate,
    InviteUserRequest,
    RemoveUserRequest,
    createEventUpdateRequest,
} from '@/model/Event';
import {
    EventFlowItem,
    EventFlowItemRequest,
    EventFlowItemResponse,
} from '@/model/event-flow/EventFlow';
import { ProfileResponse } from '@/model/Profile';
import router from '@/router';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import store from '..';

function normalizeDate(date?: Date | string | null): Date | null {
    if (!date) {
        return null;
    }

    return createDateFromUTCString(date.toString());
}

function normalizeEvent(event: EventResponse | null): EventResponse | null {
    if (!event) {
        return null;
    }

    return {
        ...event,
        selectedStartDate: normalizeDate(event.selectedStartDate) as Date,
        selectedEndDate: normalizeDate(event.selectedEndDate) as Date,
        startDate: normalizeDate(event.startDate) as Date,
        createdAt: normalizeDate(event.createdAt),
        deletedAt: normalizeDate(event.deletedAt),
    };
}

function normalizeEventFlowItems(
    eventFlowItems: EventFlowItem[],
): EventFlowItem[] {
    return eventFlowItems.map((eventFlowItem) => ({
        ...eventFlowItem,
        startedAt: normalizeDate(eventFlowItem.startedAt) as Date,
        endedAt: normalizeDate(eventFlowItem.endedAt) as Date,
    }));
}

function getSourceStartDate(
    duplicatedEvent: EventResponse,
    eventFlowItems: EventFlowItem[],
): Date {
    const earliestStartedAt = eventFlowItems.reduce<Date | null>(
        (earliestDate, eventFlowItem) => {
            if (!earliestDate || eventFlowItem.startedAt < earliestDate) {
                return eventFlowItem.startedAt;
            }

            return earliestDate;
        },
        null,
    );

    return earliestStartedAt ?? duplicatedEvent.startDate;
}

@Module({ namespaced: true, preserveState: false })
export default class EventStorage extends VuexModule {
    error: ApiError | null = null;

    events: EventResponse[] = new Array<EventResponse>();

    event: EventResponse | null = null;

    invitableUsers: ProfileResponse[] = new Array<ProfileResponse>();

    invitedUsers: ProfileResponse[] = new Array<ProfileResponse>();

    @Mutation
    setError(error: ApiError | null): void {
        this.error = error;
    }

    @Mutation
    setEvents(events: EventResponse[]): void {
        this.events = events;
    }

    @Mutation
    deleteEventFromEvents(eventId: number): void {
        if (this.events) {
            this.events = this.events.filter((event) => event.id !== eventId);
        }
    }

    @Mutation
    setEvent(event: EventResponse | null): void {
        this.event = event;
    }

    @Mutation
    setInvitableUsers(invitableUsers: ProfileResponse[]): void {
        this.invitableUsers = invitableUsers;
    }

    @Mutation
    setInvitedUsers(invitedUsers: ProfileResponse[]): void {
        this.invitedUsers = invitedUsers;
    }

    @Mutation
    removeUserFromInvitedUsers(userId: number): void {
        this.invitedUsers = this.invitedUsers.filter(
            (user) => user.id !== userId,
        );
    }

    @Action
    fetchEvents(): Promise<void | EventResponse[]> {
        return apiClient
            .get<void, EventResponse[]>('/events')
            .then((data: EventResponse[]) => {
                this.context.commit(
                    'setEvents',
                    data
                        .map((event) => normalizeEvent(event))
                        .filter(Boolean) as EventResponse[],
                );
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    create(formData: EventFormData): Promise<void | EventResponse> {
        return apiClient
            .post<EventRequest, EventResponse>(
                `/projects/${formData.projectId}/events`,
                createEventRequestFromFormData(formData),
            )
            .then((data: EventResponse) => {
                this.context.commit('setError', null);
                return Promise.resolve(normalizeEvent(data) as EventResponse);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    duplicateEvent(request: {
        projectId: number;
        eventRequest: DuplicateEventRequest;
    }): Promise<void | EventResponse> {
        return apiClient
            .post<DuplicateEventRequest, EventResponse>(
                `/projects/${request.projectId}/events/duplicate`,
                request.eventRequest,
            )
            .then((data: EventResponse) => {
                this.context.commit('setError', null);
                return Promise.resolve(normalizeEvent(data) as EventResponse);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    fetchDuplicateSource(eventId: number): Promise<DuplicateEventSource> {
        return Promise.all([
            apiClient.get<void, EventResponse>(`/events/${eventId}`),
            apiClient.post<EventFlowItemRequest, EventFlowItemResponse>(
                `/events/${eventId}/items/filters`,
                { eventId },
            ),
        ])
            .then(([event, eventFlowResponse]) => {
                const duplicatedEvent = normalizeEvent(event) as EventResponse;
                const eventFlowItems = normalizeEventFlowItems(
                    eventFlowResponse.eventItemDtoList,
                );

                this.context.commit('setError', null);

                return Promise.resolve({
                    duplicatedEvent,
                    sourceStartDate: getSourceStartDate(
                        duplicatedEvent,
                        eventFlowItems,
                    ),
                });
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    fetchEventById(id: number): Promise<void | EventResponse> {
        return apiClient
            .get<void, EventResponse>(`/events/${id}`)
            .then((data: EventResponse) => {
                this.context.commit('setEvent', normalizeEvent(data));
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setEvent', null);

                if (error?.key === 'security.permission.denied') {
                    router.push({ name: 'NotFound' });
                }

                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    fetchInvitableUsers(id: number): Promise<void | ProfileResponse[]> {
        return apiClient
            .get<void, ProfileResponse[]>(`/events/${id}/invitable`)
            .then((data: ProfileResponse[]) => {
                this.context.commit('setInvitableUsers', data);
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    fetchInvitedUsers(id: number): Promise<void | ProfileResponse[]> {
        return apiClient
            .get<void, ProfileResponse[]>(`/events/${id}/users`)
            .then((data: ProfileResponse[]) => {
                this.context.commit('setInvitedUsers', data);
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    inviteUser(inviteUserRequest: InviteUserRequest): Promise<void> {
        return apiClient
            .post<void>(`/events/${inviteUserRequest.eventId}/invite`, {
                data: inviteUserRequest.userId,
            })
            .then(() => {
                this.context.commit('setError', null);
                store.commit(
                    'customerProjectStorage/addProjectUserEventId',
                    inviteUserRequest,
                );
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    removeUser(removeUserRequest: RemoveUserRequest): Promise<void> {
        return apiClient
            .delete<void>(
                `/events/${removeUserRequest.eventId}/${removeUserRequest.userId}`,
            )
            .then(() => {
                this.context.commit('setError', null);
                this.context.commit(
                    'removeUserFromInvitedUsers',
                    removeUserRequest.userId,
                );
                store.commit(
                    'customerProjectStorage/removeProjectUserEventId',
                    removeUserRequest,
                );
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    deleteEvent(eventId: number): Promise<void> {
        return apiClient
            .delete<void>(`/events/${eventId}`)
            .then(() => {
                this.context.commit('setError', null);
                this.context.commit('deleteEventFromEvents', eventId);
                return Promise.resolve();
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    updateEvent(eventUpdate: EventUpdate): Promise<void> {
        return apiClient
            .put(
                `/events/${eventUpdate.eventId}`,
                createEventUpdateRequest(eventUpdate.eventRequest),
            )
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    updateEventFlowSettings(request: EventFlowSettingsUpdate): Promise<void> {
        return apiClient
            .put(
                `/events/${request.eventId}/event-settings`,
                request.selectedDate,
            )
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    get getError(): ApiError | null {
        return this.error;
    }

    get getEvents(): Array<EventResponse> | null {
        return this.events;
    }

    get getEvent(): EventResponse | null {
        return this.event;
    }

    get getInvitableUsers(): Array<ProfileResponse> {
        return this.invitableUsers;
    }

    get getInvitedUsers(): Array<ProfileResponse> {
        return this.invitedUsers;
    }
}
